import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson } from "@/data/api";

// Transcript egress webhook endpoints (Settings > Webhooks). The HMAC
// signing secret is returned exactly once at creation; it is never listable.

export interface WorkspaceWebhookRow {
  id: string;
  url: string;
  description: string | null;
  created_at: string | number;
  last_delivered_at: string | number | null;
}

interface WebhooksResponse {
  webhooks: WorkspaceWebhookRow[];
}

export interface CreatedWebhook {
  webhook: WorkspaceWebhookRow;
  secret: string;
}

const webhookKeys = {
  list: (workspaceSlug: string | null | undefined) =>
    ["webhooks", workspaceSlug ?? null] as const,
};

export function useWorkspaceWebhooksQuery(
  workspaceSlug: string | null | undefined,
) {
  return useQuery({
    queryKey: webhookKeys.list(workspaceSlug),
    queryFn: () => listWebhooks(workspaceSlug ?? ""),
    enabled: Boolean(workspaceSlug),
    staleTime: 15_000,
  });
}

export function useCreateWebhookMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { url: string; description?: string }) =>
      createWebhook(workspaceSlug ?? "", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: webhookKeys.list(workspaceSlug),
      });
    },
  });
}

export function useDeleteWebhookMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (webhookId: string) =>
      deleteWebhook(workspaceSlug ?? "", webhookId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: webhookKeys.list(workspaceSlug),
      });
    },
  });
}

function listWebhooks(workspaceSlug: string) {
  return apiJson<WebhooksResponse>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/webhooks`,
  );
}

function createWebhook(
  workspaceSlug: string,
  input: { url: string; description?: string },
) {
  return apiJson<CreatedWebhook>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/webhooks`,
    {
      method: "POST",
      json: input,
    },
  );
}

function deleteWebhook(workspaceSlug: string, webhookId: string) {
  return apiJson<{ ok: true }>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/webhooks/${encodeURIComponent(
      webhookId,
    )}`,
    { method: "DELETE" },
  );
}
